import { FC, Fragment, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/et'
import classNames from 'classnames'
import {
  useCalendarNav,
  useCalendarExpansion,
  useCalendarPanel,
} from 'components/contexts/CalendarContext'
import { useResponsiveCalendarWidth } from 'hooks/useResponsiveCalendarWidth'
import { useFetchCalendarDay } from 'hooks/requests/useCalendar'
import CalendarLanguageRow, {
  SLOT_WIDTH_PX,
  LABEL_WIDTH_PX,
} from 'components/molecules/CalendarLanguageRow/CalendarLanguageRow'
import ChevronLeft from 'assets/icons/chevron_left.svg?react'
import { useCurrentTimeMarker } from 'hooks/useCurrentTimeMarker'
import { useCalendarRole } from 'hooks/useCalendarRole'
import { useAuth } from 'components/contexts/AuthContext'
import { CalendarDayProvider } from 'components/contexts/CalendarDayContext'
import { useCalendarPinning } from 'hooks/useCalendarPinning'
import { useVisibleCalendarLanguages } from 'hooks/useVisibleCalendarLanguages'
import CalendarDayVendorRows from 'components/molecules/CalendarDayVendorRows/CalendarDayVendorRows'
import CalendarDayClientBookingRows from 'components/molecules/CalendarDayClientBookingRows/CalendarDayClientBookingRows'
import CalendarCollapseExpandButton from 'components/atoms/CalendarCollapseExpandButton/CalendarCollapseExpandButton'
import {
  bookedSlotsOverlap,
  mergeClientPrebookSlot,
  slotsForLanguageFromDay,
} from 'helpers/calendarDayOverlaps'
import CalendarTimeMarker from 'components/atoms/CalendarTimeMarker/CalendarTimeMarker'
import CalendarLoadingOverlay from 'components/atoms/CalendarLoadingOverlay/CalendarLoadingOverlay'
import type { BookedSlot } from 'types/calendar'
import classes from './classes.module.scss'

const DAY_START_HOUR = 8
const DAY_END_HOUR = 20
const TOTAL_SLOTS = (DAY_END_HOUR - DAY_START_HOUR) * 2

// Bold at quarter-day boundaries; regular for others
const BOLD_HOURS = new Set([8, 12, 16, 20])

// Hours to display on the time axis (every full hour)
const HOUR_LABELS = Array.from(
  { length: DAY_END_HOUR - DAY_START_HOUR },
  (_, i) => DAY_START_HOUR + i
)

function currentTimeX(dateStr: string, slotWidthPx: number): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  // new Date(y, m-1, d, h) always creates local time — no UTC/dayjs ambiguity
  const dayStartMs = new Date(y, m - 1, d, DAY_START_HOUR, 0, 0, 0).getTime()
  const dayEndMs = new Date(y, m - 1, d, DAY_END_HOUR, 0, 0, 0).getTime()
  const nowMs = Date.now()
  if (nowMs < dayStartMs || nowMs > dayEndMs) return -1
  const minutesFromStart = (nowMs - dayStartMs) / 60000
  return (minutesFromStart / 30) * slotWidthPx
}

const CalendarDayView: FC = () => {
  const {
    currentDate,
    navigatePrev,
    navigateNext,
    navigatePrevMonth,
    navigateNextMonth,
    navigateToday,
    isSearching,
  } = useCalendarNav()
  const {
    isLanguageExpanded,
    toggleLanguageExpanded,
    expandLanguageIds,
    allCollapsedOverride,
  } = useCalendarExpansion()
  const { openSidePanel, sidePanelSelection } = useCalendarPanel()
  const { t } = useTranslation()
  const { isTPM, isClient, isTranslator } = useCalendarRole()
  const { isTranslationAgency } = useAuth()
  const canInteract = isTPM || isClient
  const { handleTogglePin, pinnedCount } = useCalendarPinning()
  const dateStr = currentDate.format('YYYY-MM-DD')
  const { languages, visibleLanguages, isLoading, isError } =
    useVisibleCalendarLanguages(dateStr, dateStr)
  const isToday = currentDate.isSame(dayjs(), 'day')
  const { data: dayData } = useFetchCalendarDay(dateStr)

  /** Auto-expand client languages with overlapping bookings once per selected day. */
  const clientOverlapAutoExpandedDateRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isClient || !dayData || visibleLanguages.length === 0) return
    if (clientOverlapAutoExpandedDateRef.current === dateStr) return

    const overlapLangIds: string[] = []
    for (const lang of visibleLanguages) {
      const raw = slotsForLanguageFromDay(lang.language.id, dayData)
      if (bookedSlotsOverlap(raw)) {
        overlapLangIds.push(lang.language.id)
      }
    }
    if (overlapLangIds.length) {
      expandLanguageIds(overlapLangIds)
    }
    clientOverlapAutoExpandedDateRef.current = dateStr
  }, [isClient, dayData, dateStr, visibleLanguages, expandLanguageIds])

  // Fluid slot width: fills available container width, min 48px per 30 min
  const containerRef = useRef<HTMLDivElement>(null)
  const slotWidth = useResponsiveCalendarWidth(
    containerRef,
    LABEL_WIDTH_PX,
    TOTAL_SLOTS,
    SLOT_WIDTH_PX
  )

  // Current time marker — updates every minute
  const timeX = useCurrentTimeMarker(
    () => currentTimeX(dateStr, slotWidth),
    [dateStr, slotWidth]
  )

  // Auto-scroll to current time on mount
  const gridScrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const x = currentTimeX(dateStr, slotWidth)
    if (!gridScrollRef.current || x < 0) return
    gridScrollRef.current.scrollLeft = Math.max(0, x - 200)
  }, [])

  const monthLabel = currentDate.locale('et').format('MMMM') // e.g. "november"
  const dayLetter = currentDate.locale('et').format('dd') // e.g. "E"
  const dateShort = currentDate.format('D.MM') // e.g. "28.11"

  return (
    <div className={classes.container} ref={containerRef}>
      {(isLoading || isSearching) && (
        <CalendarLoadingOverlay searching={isSearching} />
      )}
      {/* Month row */}
      <div className={classes.dateNavMonth}>
        <div className={classes.navLeft}>
          <button
            className={classes.navBtn}
            onClick={navigatePrevMonth}
            aria-label={t('calendar.prev_month')}
          >
            <ChevronLeft className={classes.navIcon} />
          </button>
        </div>
        <div className={classes.navMonthCenter}>
          <span className={classes.monthLabel}>{monthLabel}</span>
        </div>
        <div className={classes.navRight}>
          <button
            className={classes.navBtn}
            onClick={navigateNextMonth}
            aria-label={t('calendar.next_month')}
          >
            <ChevronLeft className={classes.navIconFlip} />
          </button>
        </div>
      </div>

      {/* Day nav row */}
      <div className={classes.dateNav}>
        <div className={classes.navLeft}>
          <button
            className={classes.navBtn}
            onClick={navigatePrev}
            aria-label={t('calendar.prev_day')}
          >
            <ChevronLeft className={classes.navIcon} />
          </button>
        </div>

        <div className={classes.navCenter}>
          <span className={classes.dayLetter}>{dayLetter}</span>
          <button className={classes.dateLabel} onClick={navigateToday}>
            <span className={classes.dateShort}>{dateShort}</span>
            {isToday && <span className={classes.todayDot} />}
          </button>
        </div>

        <div className={classes.navRight}>
          <button
            className={classes.navBtn}
            onClick={navigateNext}
            aria-label={t('calendar.next_day')}
          >
            <ChevronLeft className={classes.navIconFlip} />
          </button>
        </div>
      </div>

      {/* Grid scroll area */}
      <div className={classes.gridScroll} ref={gridScrollRef}>
        {/* Time header row */}
        <div className={classes.headerRow}>
          {/* Sticky corner cell */}
          <div className={classes.cornerCell}>
            {isTPM && (
              <CalendarCollapseExpandButton languages={visibleLanguages} />
            )}
          </div>

          {/* Hour cells */}
          <div
            className={classes.timeHeader}
            style={{ width: TOTAL_SLOTS * slotWidth }}
          >
            {HOUR_LABELS.map((hour) => (
              <div
                key={hour}
                className={classNames(classes.hourCell, {
                  [classes.hourCellBold]: BOLD_HOURS.has(hour),
                })}
                style={{ left: (hour - DAY_START_HOUR) * slotWidth * 2 }}
              >
                {String(hour).padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>

        {/* Language rows */}
        <CalendarDayProvider
          value={{
            date: dateStr,
            dayStartHour: DAY_START_HOUR,
            dayEndHour: DAY_END_HOUR,
            slotWidth,
          }}
        >
          <div className={classes.rowsContainer}>
            {/* Full-height vertical hour guides */}
            {HOUR_LABELS.map((hour) => (
              <div
                key={hour}
                className={classes.hourGuide}
                style={{
                  left:
                    LABEL_WIDTH_PX + (hour - DAY_START_HOUR) * slotWidth * 2,
                }}
              />
            ))}

            {visibleLanguages.map((lang) => {
              const rawSlots = slotsForLanguageFromDay(
                lang.language.id,
                dayData
              )
              const mergedSlots = mergeClientPrebookSlot(
                lang,
                rawSlots,
                sidePanelSelection
              )
              const hasClientOverlap =
                isClient && !!dayData && bookedSlotsOverlap(mergedSlots)

              const isTPMExpanded =
                isTPM &&
                !allCollapsedOverride &&
                (lang.pinned || isLanguageExpanded(lang.language.id))

              const isClientOverlapExpanded =
                hasClientOverlap &&
                !allCollapsedOverride &&
                isLanguageExpanded(lang.language.id)

              const isExpanded = isTPM ? isTPMExpanded : isClientOverlapExpanded

              const openSlotPanel = (slot: BookedSlot) => {
                openSidePanel({
                  language: lang,
                  startIso: slot.start_at,
                  endIso: slot.end_at,
                  slot,
                })
              }

              return (
                <Fragment key={lang.language.id}>
                  <CalendarLanguageRow
                    language={lang}
                    readOnly={isTPM || !canInteract || isTranslationAgency}
                    dayData={dayData}
                    onSelectRange={
                      isClient
                        ? (langId, start, end) => {
                            const l = languages.find(
                              (l) => l.language.id === langId
                            )
                            if (l)
                              openSidePanel({
                                language: l,
                                startIso: start,
                                endIso: end,
                              })
                          }
                        : undefined
                    }
                    onClickSlot={
                      isClient || isTranslator ? openSlotPanel : undefined
                    }
                    onTogglePin={
                      canInteract && (lang.pinned || pinnedCount < 3)
                        ? () => handleTogglePin(lang.language.id)
                        : undefined
                    }
                    onToggleExpand={
                      isTPM
                        ? () => toggleLanguageExpanded(lang.language.id)
                        : hasClientOverlap
                          ? () => toggleLanguageExpanded(lang.language.id)
                          : undefined
                    }
                    isExpanded={isExpanded}
                    omitBookedSlotBlocks={
                      hasClientOverlap && !isClientOverlapExpanded
                    }
                  />
                  {isTPM && isTPMExpanded && (
                    <CalendarDayVendorRows language={lang} />
                  )}
                  {hasClientOverlap && isClientOverlapExpanded && (
                    <CalendarDayClientBookingRows
                      language={lang}
                      dayData={dayData}
                      readOnly={isTranslationAgency}
                      onSelectRange={(langId, start, end) => {
                        const l = languages.find(
                          (l) => l.language.id === langId
                        )
                        if (l)
                          openSidePanel({
                            language: l,
                            startIso: start,
                            endIso: end,
                          })
                      }}
                      onClickSlot={openSlotPanel}
                    />
                  )}
                </Fragment>
              )
            })}

            {isLoading && (
              <div className={classes.stateMessage}>
                {t('calendar.loading')}
              </div>
            )}
            {isError && (
              <div className={classes.stateMessage}>
                {t('calendar.error_loading')}
              </div>
            )}

            {isToday && timeX >= 0 && (
              <CalendarTimeMarker
                style={{ left: LABEL_WIDTH_PX + timeX, top: -40, height: 40 }}
              />
            )}
          </div>
        </CalendarDayProvider>
      </div>
    </div>
  )
}

export default CalendarDayView
